import React, { useEffect, useState } from "react";
import CardHeader from "../../components/common/CardHeader";
import AccessDenied from "../../components/common/AccessDenied";
import { useAuth } from "../../context/AuthContext";
import {
  getLoginAnnouncementSettings,
  saveLoginAnnouncementSettings,
} from "../../services/SystemAnnouncement";
import AnnouncementSettingsForm from "../../components/SystemSettings/AnnouncementSettingsForm";
import SettingsSection from "../../components/SystemSettings/SettingsSection";
import {
  EMPTY_ANNOUNCEMENT,
  getAnnouncementStatus,
  normalizeAnnouncement,
} from "../../components/SystemSettings/systemSettingsUtils";

function SystemSettings() {
  const { accessToken, checkPermission } = useAuth();
  const [announcement, setAnnouncement] = useState(EMPTY_ANNOUNCEMENT);
  const [announcementLoading, setAnnouncementLoading] = useState(true);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [savedMessage, setSavedMessage] = useState("");
  const [error, setError] = useState("");
  const [openSections, setOpenSections] = useState({
    announcement: true,
  });

  useEffect(() => {
    const load = async () => {
      if (!accessToken || !checkPermission("Zarządzanie użytkownikami")) {
        setAnnouncementLoading(false);
        return;
      }

      setError("");

      const loadAnnouncementSettings = async () => {
        try {
          const announcementSettings =
            await getLoginAnnouncementSettings(accessToken);
          setAnnouncement(normalizeAnnouncement(announcementSettings));
        } catch (err) {
          setError(
            err?.response?.data?.msg ||
              "Nie udało się pobrać komunikatu systemowego.",
          );
        } finally {
          setAnnouncementLoading(false);
        }
      };

      await loadAnnouncementSettings();
    };

    load();
  }, [accessToken, checkPermission]);

  const toggleSection = (sectionId) => {
    setOpenSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  const updateAnnouncement = (field, value) => {
    setSavedMessage("");
    setAnnouncement((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSaveAnnouncement = async () => {
    setSavedMessage("");
    setError("");

    if (
      announcement.displayFrom &&
      announcement.displayTo &&
      new Date(announcement.displayTo) < new Date(announcement.displayFrom)
    ) {
      setError(
        "Data zakończenia nie może być wcześniejsza niż data rozpoczęcia.",
      );
      return;
    }

    try {
      setSavingAnnouncement(true);
      const settings = await saveLoginAnnouncementSettings(
        announcement,
        accessToken,
      );
      setAnnouncement(normalizeAnnouncement(settings));
      setSavedMessage("Zapisano komunikat systemowy.");
    } catch (err) {
      setError(
        err?.response?.data?.msg ||
          "Nie udało się zapisać komunikatu systemowego.",
      );
    } finally {
      setSavingAnnouncement(false);
    }
  };

  if (!checkPermission("Zarządzanie użytkownikami")) {
    return <AccessDenied />;
  }

  const sections = [
    {
      id: "system-announcement-section",
      key: "announcement",
      title: "Komunikaty systemowe",
      description:
        "Komunikat pojawi się na stronie logowania tylko wtedy, gdy jest włączony, ma uzupełnioną treść i aktualna data mieści się w wybranym zakresie.",
      badge: getAnnouncementStatus(announcement),
      open: openSections.announcement,
      toggle: () => toggleSection("announcement"),
      Form: AnnouncementSettingsForm,
      formProps: {
        announcement,
        loading: announcementLoading,
        savingAnnouncement,
        updateAnnouncement,
        handleSaveAnnouncement,
      },
    },
  ];

  return (
    <>
      <CardHeader text="Ustawienia systemowe" onlyText="true" />
      <div className="container-fluid ms-4 px-4 py-3 w80">
        {error && <div className="alert alert-danger">{error}</div>}
        {savedMessage && (
          <div className="alert alert-success">{savedMessage}</div>
        )}

        {sections.map((section) => (
          <SettingsSection key={section.key} section={section} />
        ))}
      </div>
    </>
  );
}

export default SystemSettings;
